"use client"

import Image from "next/image"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { ArrowRight } from "lucide-react"
import Link from "next/link"

export interface StepItem {
  title: string
  supportText?: string
  bullets: string[]
}

interface StepsProps {
  title?: string
  subtitle?: string
  supportText?: string
  steps: StepItem[]
  buttonText?: string
  href?: string
  imageSrc?: string
  imageAlt?: string
  className?: string
}

export function Steps({
  title,
  subtitle,
  supportText,
  steps,
  buttonText,
  href,
  imageSrc,
  imageAlt,
  className,
}: StepsProps) {

  return (
    <div className={cn("w-full rounded-lg", className)}>
      <div className="container mx-auto">
        <div className="flex flex-col xl:flex-row xl:items-start xl:gap-12">
          {/* Image Section - Only visible on desktop */}
          {imageSrc && imageAlt && (
            <div className="hidden xl:block w-full xl:w-1/2 mb-8 xl:mb-0">
              <Image
                src={imageSrc}
                alt={imageAlt}
                width={500}
                height={400}
                className="w-full h-auto object-contain"
                priority
              />
            </div>
          )}

          {/* Content Section - Full width on mobile */}
          <div className={cn("w-full", imageSrc && imageAlt ? "xl:w-1/2" : "w-full")}>
            {/* Title and Subtitle - Optional */}
            {title && <h2 className="text-2xl xl:text-3xl font-bold text-fb-primary mb-4">{title}</h2>}
            {subtitle && <p className="text-slate-900 text-xl mb-6">{subtitle}</p>}
            {supportText && (
              <p className="text-base text-slate-900 mb-6">{supportText}</p>
            )}

            {/* Steps - Required */}
            <div className="space-y-0">
              {steps.map((step, index) => (
                <div key={index} className="flex">
                  {/* Step Number and Dotted Line Column */}
                  <div className="mr-4 flex-shrink-0 flex flex-col items-center">
                    {/* Step Number - Fixed size */}
                    <div className="flex items-center justify-center w-[30px] h-[30px] p-1 rounded-lg bg-fb-primary text-white font-bold text-lg flex-shrink-0">
                      {index + 1}
                    </div>

                    {/* Dotted line connector - only between steps, not after the last one */}
                    {index < steps.length - 1 && (
                      <div className="border-l-2 border-dotted border-fb-primary flex-1 w-0"></div>
                    )}
                  </div>

                  {/* Step Content */}
                  <div className="flex-1 pb-10 xl:pb-12">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">{step.title}</h3>
                    {step.supportText && (
                      <p className="text-gray-500 mb-2">{step.supportText}</p>
                    )}
                    <ul className="space-y-2 list-disc pl-4">
                      {step.bullets.map((bullet, bulletIndex) => (
                        <li key={bulletIndex} className="text-gray-600">
                          {bullet}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>

            {/* Button - Optional */}
            {buttonText && href && (
              <div className="mt-8">
                <Button
                  asChild
                  className="bg-fb-primary text-white px-6 py-2 rounded-md"
                >
                  <Link href={href}>
                    {buttonText} <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}